import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { useTranslation } from 'react-i18next';
import './forecast.css';
import { fitsDecimals, monthLabel, numKeyDown, yearMonthIndexes } from './forecastUtils';

interface Props {
    show: boolean;
    line: any;
    forecast: any;
    readOnly: boolean;
    saving: boolean;
    onClose: () => void;
    /** units: the full typed-units map when it changed (Units × Price lines), else null. */
    onSave: (values: { month_index: number; amount: number | null }[], units: Record<string, number> | null) => void;
}

/**
 * Typed monthly amounts for one line, one forecast year at a time. Units × Price
 * lines can also type units sold per month (later months continue from them).
 * For a Direct Monthly Entry line these ARE the line's values; for a formula
 * line they override the calculated value for that month only. A blank month
 * means "no typed value" (cleared on save).
 */
const MonthlyValuesModal: React.FC<Props> = ({ show, line, forecast, readOnly, saving, onClose, onSave }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const [year, setYear] = useState<number>(1);
    const [values, setValues] = useState<Record<number, string>>({});
    const [original, setOriginal] = useState<Record<number, string>>({});
    const [copyNext, setCopyNext] = useState<boolean>(false);
    const [mode, setMode] = useState<'amount' | 'units'>('amount');
    const [units, setUnits] = useState<Record<number, string>>({});
    const [originalUnits, setOriginalUnits] = useState<Record<number, string>>({});
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!show || !line) return;
        const v: Record<number, string> = {};
        (line.monthly_values || []).forEach((mv: any) => { v[mv.month_index] = String(mv.amount); });
        setValues(v);
        setOriginal(v);
        const u: Record<number, string> = {};
        Object.entries(line.params?.units_by_month || {}).forEach(([k, n]) => { u[Number(k)] = String(n); });
        setUnits(u);
        setOriginalUnits(u);
        setMode(line.method === 'units_price' ? 'units' : 'amount');
        setYear(Math.floor(((line.start_month_index || 1) - 1) / 12) + 1);
        setCopyNext(false);
        setError('');
    }, [show, line]);

    if (!line || !forecast) return null;
    /** Calendar year a forecast year starts in, e.g. 2027 for a Year 1 starting Oct 2027. */
    const yearLabel = (n: number): string =>
        String(Number(forecast.start_year) + Math.floor((Number(forecast.start_month) - 1 + (n - 1) * 12) / 12));
    const isDirect = line.method === 'direct_monthly';
    const hasUnits = line.method === 'units_price';
    const months = yearMonthIndexes(year);
    const current = mode === 'units' ? units : values;
    const setCurrent = mode === 'units' ? setUnits : setValues;

    const norm = (raw: string | undefined) => {
        const s = (raw ?? '').toString().trim();
        return s === '' || Number.isNaN(Number(s)) ? null : Number(s);
    };

    /** Copies this year's 12 entries into every later forecast year. */
    const applyToLaterYears = (src: Record<number, string>) => {
        const next = { ...src };
        for (let y = year + 1; y <= 5; y++) {
            yearMonthIndexes(y).forEach((idx, i) => { next[idx] = src[months[i]] ?? ''; });
        }
        return next;
    };

    /** Sends every month changed in any year (edits survive switching years). */
    const save = () => {
        // Repeat this year's entries (of the mode being edited) into every later forecast year.
        const nextValues = mode === 'amount' && copyNext ? applyToLaterYears(values) : values;
        const nextUnits = mode === 'units' && copyNext ? applyToLaterYears(units) : units;
        const anyNeg = (src: Record<number, string>) => Object.values(src).some((v) => {
            const n = norm(v);
            return n !== null && n < 0;
        });
        if (anyNeg(nextUnits) || anyNeg(nextValues)) {
            return setError(String(t('monthly.noNegative', 'Monthly values cannot be negative.')));
        }
        const out: { month_index: number; amount: number | null }[] = [];
        let unitsChanged = false;
        const unitMap: Record<string, number> = {};
        for (let idx = 1; idx <= 60; idx++) {
            if (norm(nextValues[idx]) !== norm(original[idx])) out.push({ month_index: idx, amount: norm(nextValues[idx]) });
            if (norm(nextUnits[idx]) !== norm(originalUnits[idx])) unitsChanged = true;
            const n = norm(nextUnits[idx]);
            if (n !== null) unitMap[String(idx)] = n;
        }
        onSave(out, unitsChanged ? unitMap : null);
    };

    const total = months.reduce((sum, idx) => sum + (Number(current[idx]) || 0), 0);

    return (
        <Modal show={show} onHide={onClose} size="lg" centered className="fyf-modal">
            <Modal.Header closeButton>
                <Modal.Title className="h6 mb-0">
                    {t('monthly.title', 'Monthly values')} — {line.account_name}{line.description ? ` / ${line.description}` : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {hasUnits && (
                    <div className="fyf-year-tabs mb-2">
                        <button type="button" className={mode === 'units' ? 'on' : ''} onClick={() => setMode('units')}>
                            {t('monthly.modeUnits', 'Units sold')}
                        </button>
                        <button type="button" className={mode === 'amount' ? 'on' : ''} onClick={() => setMode('amount')}>
                            {t('monthly.modeAmount', 'Amounts ($)')}
                        </button>
                    </div>
                )}
                <p className="fyf-note mb-3">
                    {mode === 'units'
                        ? t('monthly.unitsHint', 'Type the units sold for any month. Later months continue from the typed month using the growth % and units added each month. Leave a month blank to calculate it. Linked COGS uses these units.')
                        : isDirect
                        ? t('monthly.directHint', 'Enter the amount for each month. Expense and deduction amounts are entered as positive numbers.')
                        : t('monthly.overrideHint', 'Amounts entered here replace the calculated value for that month only. Leave a month blank to use the calculation.')}
                </p>
                <div className="fyf-year-tabs mb-3">
                    {[1, 2, 3, 4, 5].map((y) => (
                        <button key={y} type="button"
                            className={y === year ? 'on' : ''}
                            onClick={() => setYear(y)}>
                            {yearLabel(y)}
                        </button>
                    ))}
                </div>
                <div className="row g-2">
                    {months.map((idx) => (
                        <div className="col-6 col-md-3" key={idx}>
                            <Form.Label className="small mb-1">{monthLabel(idx, forecast.start_month, forecast.start_year)}</Form.Label>
                            <Form.Control
                                type="number"
                                step={mode === 'units' ? 'any' : '0.01'}
                                min={0}
                                onKeyDown={numKeyDown}
                                size="sm"
                                aria-label={monthLabel(idx, forecast.start_month, forecast.start_year)}
                                disabled={readOnly || idx < (line.start_month_index || 1)}
                                value={current[idx] ?? ''}
                                onChange={(e) => { if (fitsDecimals(e.target.value)) { setCurrent((v) => ({ ...v, [idx]: e.target.value })); setError(''); } }}
                            />
                        </div>
                    ))}
                </div>
                {error && <div className="fyf-error mt-3">{error}</div>}
                <div className="d-flex justify-content-between align-items-center mt-3">
                    {year < 5 && !readOnly ? (
                        <Form.Check type="checkbox" id="copy-next"
                            label={t('monthly.copyNext', 'Repeat these 12 months in later years')}
                            checked={copyNext}
                            onChange={(e) => {
                                const on = e.target.checked;
                                setCopyNext(on);
                                // Fill the later years right away so the copied values are visible on their tabs.
                                if (on) { setValues(applyToLaterYears(values)); setUnits(applyToLaterYears(units)); }
                            }} />
                    ) : <span />}
                    <span className="small fw-bold">
                        {mode === 'units'
                            ? `${t('monthly.yearUnits', '{{n}} units typed', { n: yearLabel(year) })}: ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                            : `${t('monthly.yearTotal', '{{n}} total', { n: yearLabel(year) })}: $${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                    </span>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="fyf-secondary" onClick={onClose}>
                    {t('common.close', 'Close')}
                </button>
                {!readOnly && (
                    <button type="button" className="fyf-primary" disabled={saving} onClick={save}>
                        {saving ? <Spinner animation="border" size="sm" /> : t('monthly.save', 'Save')}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default MonthlyValuesModal;
